import { Link } from 'react-router-dom';
import { useLazyImage } from '../../../hooks/useLazyImage';
import { buildWebPSrcSet, toWebP } from '../../../lib/imageUtils';

interface FeaturedArticleProps {
  post: any;
}

export default function FeaturedArticle({ post }: FeaturedArticleProps) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });
  const imageUrl = post.heroImage || post.image || '';

  const parsedDate = post.date && !isNaN(Date.parse(post.date))
    ? new Date(post.date)
    : null;
  const displayDate = parsedDate
    ? parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : post.date || '';
  const isoDate = parsedDate ? parsedDate.toISOString().split('T')[0] : '';

  return (
    <div className="mb-8 sm:mb-10">
      {/* Section label */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 bg-[#E8B84B] text-[#1C2B1E] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
          <i className="ri-star-fill text-sm"></i>
          Featured Article
        </div>
        <div className="flex-1 h-px bg-[#E8E2D6]" />
      </div>

      {/* Card */}
      <article
        aria-label={`Featured: ${post.title}`}
        className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden hover:shadow-2xl transition-all duration-300 group"
      >
        <Link to={`/blog/${post.slug}`} className="flex flex-col lg:flex-row">
          {/* Image — wider on desktop */}
          <div
            ref={ref}
            className="relative w-full lg:w-[55%] aspect-[16/9] lg:aspect-auto lg:min-h-[320px] overflow-hidden bg-gray-100 flex-shrink-0"
          >
            {isVisible && imageUrl ? (
              <picture>
                <source
                  type="image/webp"
                  srcSet={buildWebPSrcSet(imageUrl)}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                />
                <img
                  src={toWebP(imageUrl, 900)}
                  alt={`${post.title} — featured article image — Rockhound Auto Blog`}
                  title={`${post.title} — Rockhound Auto`}
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                  width={900}
                  height={506}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </picture>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] animate-pulse" aria-hidden="true" />
            )}

            {/* Gradient overlay on image */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/10" />

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <span className="bg-[#E8B84B] text-[#1C2B1E] px-3 py-1.5 rounded-full font-bold text-xs uppercase tracking-wide">
                {post.category}
              </span>
              {post.featured && (
                <span className="bg-white/90 text-[#1C2B1E] px-3 py-1.5 rounded-full font-bold text-xs flex items-center gap-1.5 shadow-sm">
                  <span aria-hidden="true">🧑‍🦲🧑‍🦲</span>
                  Bald Guys Pick
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between">
            <div>
              {/* Meta */}
              <div className="flex items-center gap-4 text-xs text-[#4A5568] mb-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-[#E8B84B]" aria-hidden="true"></i>
                  {isoDate ? (
                    <time dateTime={isoDate}>{displayDate}</time>
                  ) : (
                    <span>{displayDate}</span>
                  )}
                </div>
                {post.readTime && (
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-[#E8B84B]" aria-hidden="true"></i>
                    <span>{post.readTime}</span>
                  </div>
                )}
              </div>

              {/* Title */}
              <h2 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-[#1C2B1E] mb-3 group-hover:text-[#E8B84B] transition-colors leading-tight">
                {post.title}
              </h2>

              {/* Excerpt — more lines since this is featured */}
              <p className="text-[#374151] text-sm sm:text-base leading-relaxed mb-5 line-clamp-4">
                {post.excerpt}
              </p>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.slice(0, 4).map((tag: string) => (
                    <span
                      key={tag}
                      className="bg-[#F7F5F0] text-[#4A5568] text-xs px-3 py-1 rounded-full border border-[#E8E2D6]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-[#E8E2D6]">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 bg-[#E8B84B] rounded-full flex items-center justify-center text-[#1C2B1E] font-bold text-sm flex-shrink-0"
                  aria-hidden="true"
                >
                  {post.author ? post.author.charAt(0) : 'R'}
                </div>
                <span className="font-medium text-[#1C2B1E] text-sm">{post.author}</span>
              </div>

              <span className="inline-flex items-center gap-2 bg-[#1C2B1E] group-hover:bg-[#E8B84B] text-white group-hover:text-[#1C2B1E] font-bold text-sm px-5 py-2.5 rounded-full transition-all whitespace-nowrap">
                Read Article
                <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
              </span>
            </div>
          </div>
        </Link>
      </article>
    </div>
  );
}
